/**
 * PortfolioHeatmapPanel — grid of debtor warning cards shown on /monitoring.
 *
 * Consumes GET /api/monitoring/portfolio-warnings. Filter chips: All / Critical /
 * Warning / Info. Clicking a card navigates to the debtor's profile.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Flame, Info, ShieldAlert, ShieldCheck } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import JeaCard from "@/components/jea/JeaCard";
import { cn } from "@/lib/utils";
import { centsToUSD } from "@/lib/dates";

const CRITICAL_LEVELS = new Set(["emergency", "critical"]);
const WARNING_LEVELS = new Set(["urgent", "recommended"]);
const INFO_LEVELS = new Set(["info"]);

const severityStyle = {
  emergency: { ring: "border-danger/70", chip: "bg-danger text-white", Icon: Flame },
  critical: { ring: "border-danger/60", chip: "bg-danger/80 text-white", Icon: AlertTriangle },
  urgent: { ring: "border-warning/60", chip: "bg-warning text-[#091424]", Icon: ShieldAlert },
  recommended: { ring: "border-accent-pink/60", chip: "bg-accent-pink text-[#091424]", Icon: ShieldAlert },
  info: { ring: "border-border-default", chip: "bg-bg-tertiary text-text-secondary", Icon: Info },
};

const clean = { ring: "border-success/50", chip: "bg-success/20 text-success", Icon: ShieldCheck };

const bandOf = (severity) => {
  if (CRITICAL_LEVELS.has(severity)) return "critical";
  if (WARNING_LEVELS.has(severity)) return "warning";
  if (INFO_LEVELS.has(severity)) return "info";
  return "clean";
};

const HeatmapCard = ({ row, onOpen }) => {
  const mu = row.most_urgent;
  const style = mu ? severityStyle[mu.severity] || severityStyle.info : clean;
  const StyleIcon = style.Icon;
  return (
    <button
      type="button"
      onClick={() => onOpen(row)}
      className={cn(
        "text-left w-full rounded-[12px] border p-4 bg-bg-secondary/60 hover:bg-bg-tertiary transition-colors group",
        style.ring,
      )}
      data-testid={`heatmap-card-${row.debtor_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-white font-semibold text-[14px] truncate">{row.debtor_name || "—"}</div>
          <div className="text-[11px] text-text-secondary">
            {row.state || "—"}
            {row.hard_cutoff && <span className="ml-1 text-danger font-bold">· HARD CUTOFF</span>}
          </div>
        </div>
        <StyleIcon size={16} className="shrink-0 text-text-secondary group-hover:text-white" />
      </div>
      {mu ? (
        <div>
          <div className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]", style.chip)}>
            {mu.severity} · {mu.days_until != null ? `${mu.days_until}d` : "—"}
          </div>
          <div className="text-[12px] text-text-secondary mt-2 line-clamp-2">{mu.message}</div>
        </div>
      ) : (
        <div>
          <div className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]", clean.chip)}>
            No warnings
          </div>
          <div className="text-[12px] text-text-secondary mt-2">Portfolio in good standing.</div>
        </div>
      )}
      <div className="mt-3 text-[11px] text-text-secondary jea-mono">
        {row.amount_cents ? centsToUSD(row.amount_cents) : ""}
      </div>
    </button>
  );
};

const FILTERS = [
  { key: "all",      label: "All" },
  { key: "critical", label: "Critical" },
  { key: "warning",  label: "Warning" },
  { key: "info",     label: "Info" },
];

export const PortfolioHeatmapPanel = ({ testid = "portfolio-heatmap-panel" }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    apiClient
      .get("/monitoring/portfolio-warnings")
      .then(({ data }) => setRows(data.debtors || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => {
      const b = bandOf(r.most_urgent?.severity);
      return b === filter;
    });
  }, [rows, filter]);

  const counts = useMemo(() => {
    const c = { all: rows.length, critical: 0, warning: 0, info: 0 };
    for (const r of rows) {
      const b = bandOf(r.most_urgent?.severity);
      if (b in c) c[b] += 1;
    }
    return c;
  }, [rows]);

  return (
    <JeaCard data-testid={testid}>
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-white font-semibold text-[16px]">Portfolio Warnings Heatmap</div>
          <div className="text-[12px] text-text-secondary">
            The most urgent warning per active judgment. Click a card to jump into the profile.
          </div>
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-[0.06em] border transition-all",
                filter === f.key
                  ? "bg-accent-pink text-[#091424] border-transparent"
                  : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60",
              )}
              data-testid={`heatmap-filter-${f.key}`}
            >
              {f.label} <span className="opacity-70 ml-1">{counts[f.key] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-[12px] border border-border-default p-4 min-h-[120px] animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-text-secondary text-[13px]">No judgments match this filter.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((row) => (
            <HeatmapCard key={row.debtor_id} row={row} onOpen={(r) => nav(`/debtors/${r.debtor_id}`)} />
          ))}
        </div>
      )}
    </JeaCard>
  );
};

export default PortfolioHeatmapPanel;
